import React, { useEffect, useRef, useState, useContext } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import ProfileUpdateForm from "../pages/ProfileUpdateForm";
import api from "../utils/base_url";
import { AuthContext } from "../context/AuthContext";
import WhatsAppWidget from "../pages/WhatsAppWidget";
import InstagramWidget from "../pages/Instagramwidget";
import "../styles/index.css";
import "../styles/Navbar.css";
import toast from "react-hot-toast";
import NotificationPanel from "./notificationsPanel";

/* ── helpers ── */
const sortByOrder = (arr) =>
  [...arr].sort((a, b) => {
    const getN = (v) => {
      if (v === null || v === undefined || v === "") return 9999;
      const n = Number(v);
      return isNaN(n) ? 9999 : n;
    };
    const diff = getN(a.order) - getN(b.order);
    return diff !== 0 ? diff : (a.category_name || "").localeCompare(b.category_name || "");
  });

const getProfileImage = (user) => {
  const cd = user?.customer_details;
  if (!cd) return "/assets/Profile.webp";
  if (user.login_method === "google" && cd.google_profile_image) return cd.google_profile_image;
  if (cd.profile_image) return cd.profile_image;
  return "/assets/Profile.webp";
};

const Navbar = () => {
  const state = useSelector((s) => s.handleCart);
  const navigate = useNavigate();
  const menuRef = useRef();
  const { user, setUser } = useContext(AuthContext);

  /* desktop dropdown */
  const [openMenu, setOpenMenu] = useState(false);
  /* mobile drawer */
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  /* categories sub-accordion in drawer */
  const [showCategories, setShowCategories] = useState(false);
  /* notifications */
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifPanelOpen, setNotifPanelOpen] = useState(false);
  /* categories */
  const [categories, setCategories] = useState([]);
  /* feedback */
  const [showModal, setShowModal] = useState(false);
  const [feedbackProduct, setFeedbackProduct] = useState(null);
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [comment, setComment] = useState("");
  const [pendingReviewCallback, setPendingReviewCallback] = useState(null);

  /* ── data fetching ── */
  useEffect(() => {
    api.get("categorylist/")
      .then((r) => setCategories(r.data.data || []))
      .catch((e) => console.error("Category fetch error", e));
  }, []);

  useEffect(() => {
    if (!user) return;
    const fetch = async () => {
      try {
        const r = await api.get(`customer-notifications/${user.customer_details.id}/`);
        if (r.data.success) {
          setNotifications(r.data.data || []);
          setUnreadCount(r.data.total || 0);
        }
      } catch (e) { console.error(e); }
    };
    fetch();
    const t = setInterval(fetch, 60000);
    return () => clearInterval(t);
  }, [user]);

  /* close desktop dropdown on outside click */
  useEffect(() => {
    const handler = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setOpenMenu(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  /* ── actions ── */
  const handleLogout = async () => {
    try { await api.post("logout/", { refresh: sessionStorage.getItem("refresh") }); }
    catch (e) { console.error(e); }
    sessionStorage.clear();
    setUser(null);
    navigate("/");
    setIsDrawerOpen(false);
  };

  const openFeedbackModal = async (item, cb) => {
    const id = item?.product || item?.product_id || item?.id;
    if (!id) return toast.error("Invalid product");
    setPendingReviewCallback(() => cb);
    setFeedbackProduct({ id, name: item?.product_name || item?.product?.product_name || "Product" });
    setRating(0);
    setComment("");
    setShowModal(true);
    try {
      const r = await api.get(`feedback/${id}/`);
      if (r.data?.data) { setRating(Number(r.data.data.rating)); setComment(r.data.data.comment || ""); }
    } catch (e) { if (e.response?.status !== 404) console.warn(e); }
  };

  const submitFeedback = async () => {
    if (!feedbackProduct?.id) return toast.error("Invalid product");
    if (!rating) return toast.error("Please select a star rating");
    try {
      await api.post(`feedback/${feedbackProduct.id}/`, { rating, comment });
      toast.success("Feedback submitted!");
      if (pendingReviewCallback) { pendingReviewCallback(); setPendingReviewCallback(null); }
      setShowModal(false);
    } catch (e) { console.error(e); toast.error("Failed to submit feedback"); }
  };

  const sorted = sortByOrder(categories);

  /* ── nav links config ── */
  const mainLinks = [
    { label: "Home", to: "/" },
    { label: "Products", to: "/product" },
    { label: "Combos", to: "/combos" },
    { label: "About", to: "/about" },
    { label: "Contact", to: "/contact" },
  ];

  /* ── Icon components (reuse Font Awesome already in project) ── */

  return (
    <>
      {/* ════════════════════════════════════════════
          DESKTOP NAVBAR (lg+)
      ════════════════════════════════════════════ */}
      <nav className="navbar d-none d-lg-flex">
        <div className="container-fluid px-4">

          {/* Brand */}
          <NavLink to="/" className="navbar-brand me-4">
            <img
              src={`${process.env.PUBLIC_URL}/mainlogo.png`}
              alt="Logo"
              className="navbar-logo"
            />
          </NavLink>

          {/* Social widgets */}
          <WhatsAppWidget inNavbar={true} />
          <InstagramWidget inNavbar={true} />

          {/* Nav links */}
          <ul className="navbar-nav mx-auto d-flex flex-row gap-1">
            {mainLinks.map(({ label, to }) => (
              <li className="nav-item" key={label}>
                <NavLink
                  to={to}
                  end={to === "/"}
                  className={({ isActive }) =>
                    `nav-link${isActive ? " active" : ""}`
                  }
                >
                  {label}
                </NavLink>
              </li>
            ))}
          </ul>

          {/* Right actions */}
          <div className="d-flex align-items-center gap-2">

            {/* Search */}
            <button
              className="nav-icon-btn"
              onClick={() => navigate("/search")}
              title="Search"
              aria-label="Search products"
            >
              <i className="fa fa-search" aria-hidden="true"></i>
            </button>

            {/* Wishlist */}
            {user && (
              <button
                className="nav-icon-btn"
                onClick={() => navigate("/favoriteProducts")}
                title="Wishlist"
                aria-label="My wishlist"
              >
                <i className="fa fa-heart" aria-hidden="true" style={{ color: "var(--color-danger)" }}></i>
              </button>
            )}

            {/* Notifications */}
            {user && (
              <button
                className="nav-icon-btn"
                onClick={() => setNotifPanelOpen(true)}
                title="Notifications"
                aria-label={`Notifications${unreadCount > 0 ? `, ${unreadCount} unread` : ""}`}
              >
                <i className="fa fa-bell" aria-hidden="true"></i>
                {unreadCount > 0 && <span className="nav-badge">{unreadCount}</span>}
              </button>
            )}

            {/* Cart */}
            <NavLink to="/cart" className="nav-cart-btn" aria-label={`Cart, ${state.length} items`}>
              <i className="fa fa-shopping-cart" aria-hidden="true"></i>
              Cart
              {state.length > 0 && <span className="nav-badge">{state.length}</span>}
            </NavLink>

            {/* Auth / Profile */}
            {user ? (
              <div className="position-relative" ref={menuRef}>
                <button
                  className="nav-profile-btn"
                  onClick={() => setOpenMenu((v) => !v)}
                  aria-expanded={openMenu}
                  aria-label="User menu"
                >
                  <img
                    src={getProfileImage(user)}
                    alt={user.customer_details?.full_name || "User"}
                    referrerPolicy="no-referrer"
                    className="nav-profile-avatar"
                  />
                  <span className="user-name-truncate">
                    {user.customer_details?.full_name || "User"}
                  </span>
                  <i className="fa fa-chevron-down nav-profile-chevron" aria-hidden="true"></i>
                </button>

                {openMenu && (
                  <div className="nav-dropdown">
                    <button
                      className="nav-dropdown-item"
                      data-bs-toggle="modal"
                      data-bs-target="#profileModal"
                      onClick={() => setOpenMenu(false)}
                    >
                      <i className="fa fa-user me-2" aria-hidden="true"></i> Profile
                    </button>
                    <button
                      className="nav-dropdown-item"
                      onClick={() => { navigate("/orderhistory"); setOpenMenu(false); }}
                    >
                      <i className="fa fa-box me-2" aria-hidden="true"></i> Order History
                    </button>
                    <hr className="nav-dropdown-divider" />
                    <button
                      className="nav-dropdown-item danger"
                      onClick={() => { handleLogout(); setOpenMenu(false); }}
                    >
                      <i className="fa fa-sign-out-alt me-2" aria-hidden="true"></i> Logout
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div className="d-flex align-items-center gap-2">
                <NavLink to="/login" className="nav-login-btn">
                  <i className="fa fa-sign-in-alt" aria-hidden="true"></i> Login
                </NavLink>
                <NavLink to="/register" className="nav-register-btn">
                  <i className="fa fa-user-plus" aria-hidden="true"></i> Register
                </NavLink>
              </div>
            )}
          </div>

        </div>
      </nav>

      {/* ════════════════════════════════════════════
          MOBILE TOP BAR (< lg)
      ════════════════════════════════════════════ */}
      <div className="mobile-navbar d-flex d-lg-none">

        {/* Left: hamburger + search */}
        <div className="mobile-nav-left">
          <button
            className="mobile-menu-btn"
            onClick={() => setIsDrawerOpen(true)}
            aria-label="Open menu"
          >
            <i className="fa fa-bars" aria-hidden="true"></i>
          </button>

          <button
            className="mobile-icon-btn"
            onClick={() => navigate("/search")}
            aria-label="Search"
          >
            <i className="fa fa-search" aria-hidden="true"></i>
          </button>
        </div>

        {/* Right: social, wishlist, bell, cart */}
        <div className="mobile-nav-right">
          <WhatsAppWidget inNavbar={true} />
          <InstagramWidget inNavbar={true} />

          <button
            className="mobile-icon-btn"
            onClick={() => navigate("/favoriteProducts")}
            aria-label="Wishlist"
          >
            <i className="fa fa-heart" aria-hidden="true" style={{ color: "var(--color-danger)" }}></i>
          </button>

          {user && (
            <button
              className="mobile-icon-btn"
              onClick={() => setNotifPanelOpen(true)}
              aria-label="Notifications"
            >
              <i className="fa fa-bell" aria-hidden="true"></i>
              {unreadCount > 0 && <span className="nav-badge">{unreadCount}</span>}
            </button>
          )}

          <button
            className="mobile-icon-btn"
            onClick={() => navigate("/cart")}
            aria-label={`Cart, ${state.length} items`}
          >
            <i className="fa fa-shopping-cart" aria-hidden="true"></i>
            {state.length > 0 && <span className="nav-badge">{state.length}</span>}
          </button>
        </div>
      </div>

      {/* ════════════════════════════════════════════
          MOBILE DRAWER
      ════════════════════════════════════════════ */}
      {isDrawerOpen && (
        <>
          <div
            className="drawer-backdrop"
            onClick={() => setIsDrawerOpen(false)}
            aria-hidden="true"
          />

          <aside className="drawer" aria-label="Navigation menu">

            {/* Header */}
            <div className="drawer-header">
              <img
                src={`${process.env.PUBLIC_URL}/mainlogo.png`}
                alt="Logo"
                style={{ height: "32px", width: "auto" }}
              />
              <button
                className="drawer-close-btn"
                onClick={() => setIsDrawerOpen(false)}
                aria-label="Close menu"
              >
                ×
              </button>
            </div>

            {/* User profile block */}
            {user && (
              <div className="drawer-profile">
                <img
                  src={getProfileImage(user)}
                  alt={user.customer_details?.full_name || "User"}
                  referrerPolicy="no-referrer"
                  className="drawer-avatar"
                />
                <div>
                  <div className="drawer-user-name">
                    {user.customer_details?.full_name || "User"}
                  </div>
                  <div className="drawer-user-sub">Signed in</div>
                </div>
              </div>
            )}

            {/* Nav links */}
            <nav className="drawer-nav">
              <div className="drawer-section-label">Menu</div>

              {/* Static links */}
              {[
                { label: "Home",    to: "/",        icon: "fa-home" },
                { label: "About",   to: "/about",   icon: "fa-info-circle" },
                { label: "Contact", to: "/contact", icon: "fa-envelope" },
                { label: "Combo", to: "/combo", icon: "fa-box-open" },
              ].map(({ label, to, icon }) => (
                <NavLink
                  key={label}
                  to={to}
                  end={to === "/"}
                  className={({ isActive }) =>
                    `drawer-nav-link${isActive ? " active" : ""}`
                  }
                  onClick={() => setIsDrawerOpen(false)}
                >
                  <span className="drawer-nav-link-left">
                    <span className="drawer-nav-link-icon">
                      <i className={`fa ${icon}`} aria-hidden="true"></i>
                    </span>
                    {label}
                  </span>
                </NavLink>
              ))}

              {/* Products accordion */}
              <button
                className="drawer-nav-link"
                onClick={() => setShowCategories((v) => !v)}
                aria-expanded={showCategories}
              >
                <span className="drawer-nav-link-left">
                  <span className="drawer-nav-link-icon">
                    <i className="fa fa-leaf" aria-hidden="true"></i>
                  </span>
                  Products
                </span>
                <i
                  className={`fa fa-chevron-${showCategories ? "up" : "down"}`}
                  style={{ fontSize: "10px", color: "var(--text-muted)" }}
                  aria-hidden="true"
                ></i>
              </button>

              {showCategories && (
                <div className="drawer-subcategory">
                  {sorted.map((cat) => (
                    <button
                      key={cat.id}
                      className="drawer-subcategory-item"
                      onClick={() => {
                        navigate(`/category/${cat.id}`);
                        setIsDrawerOpen(false);
                        setShowCategories(false);
                      }}
                    >
                      {cat.category_name}
                    </button>
                  ))}
                </div>
              )}

              {/* Account links (logged in) */}
              {user && (
                <>
                  <hr className="drawer-divider" />
                  <div className="drawer-section-label">Account</div>

                  <button
                    className="drawer-nav-link"
                    data-bs-toggle="modal"
                    data-bs-target="#profileModal"
                    onClick={() => setIsDrawerOpen(false)}
                  >
                    <span className="drawer-nav-link-left">
                      <span className="drawer-nav-link-icon">
                        <i className="fa fa-user" aria-hidden="true"></i>
                      </span>
                      Profile
                    </span>
                  </button>

                  <button
                    className="drawer-nav-link"
                    onClick={() => { navigate("/orderhistory"); setIsDrawerOpen(false); }}
                  >
                    <span className="drawer-nav-link-left">
                      <span className="drawer-nav-link-icon">
                        <i className="fa fa-box" aria-hidden="true"></i>
                      </span>
                      Order History
                    </span>
                  </button>
                </>
              )}
            </nav>

            {/* Footer: logout or auth buttons */}
            <div className="drawer-footer">
              {user ? (
                <button
                  className="drawer-logout-btn"
                  onClick={handleLogout}
                >
                  <i className="fa fa-sign-out-alt" aria-hidden="true"></i>
                  Logout
                </button>
              ) : (
                <div className="drawer-auth-row">
                  <button
                    className="drawer-auth-btn login"
                    onClick={() => { navigate("/login"); setIsDrawerOpen(false); }}
                  >
                    Login
                  </button>
                  <button
                    className="drawer-auth-btn register"
                    onClick={() => { navigate("/register"); setIsDrawerOpen(false); }}
                  >
                    Register
                  </button>
                </div>
              )}
            </div>
          </aside>
        </>
      )}

      {/* ── Profile modal ── */}
      {user && <ProfileUpdateForm />}

      {/* ── Notification panel ── */}
      {user && (
        <NotificationPanel
          isOpen={notifPanelOpen}
          onClose={() => setNotifPanelOpen(false)}
          user={user}
          notifications={notifications}
          setNotifications={setNotifications}
          unreadCount={unreadCount}
          setUnreadCount={setUnreadCount}
          openFeedbackModal={openFeedbackModal}
        />
      )}

      {/* ── Feedback modal ── */}
      {showModal && (
        <div className="feedback-modal">
          <div className="feedback-content">
            <h5 className="feedback-title">
              Review — {feedbackProduct?.name}
            </h5>

            <div className="stars">
              {[1, 2, 3, 4, 5].map((s) => (
                <span
                  key={s}
                  className={`star${s <= (hover || rating) ? " active" : ""}`}
                  onClick={() => setRating(s)}
                  onMouseEnter={() => setHover(s)}
                  onMouseLeave={() => setHover(rating)}
                  role="button"
                  aria-label={`${s} star${s > 1 ? "s" : ""}`}
                >
                  ★
                </span>
              ))}
            </div>

            <textarea
              className="form-control mb-4"
              rows="3"
              maxLength="200"
              placeholder="Share your experience (max 200 characters)"
              value={comment}
              onChange={(e) => setComment(e.target.value.slice(0, 200))}
            />

            <div className="d-flex gap-2 justify-content-end">
              <button
                className="btn btn-ghost"
                onClick={() => setShowModal(false)}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={submitFeedback}
              >
                Submit Review
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Navbar;